import { useEffect, useMemo, useRef, useState } from "react";
import { T } from "./tokens";

interface LetterBankProps {
  /** The target answer. Spaces define word boundaries (visual gap only).
   *  Diacritics and quote marks are stripped before tile + slot generation. */
  correctText: string;
  /** Locale: "he" or "en" — controls the filler-letter alphabet. */
  locale: "he" | "en";
  /** Increment from parent to signal a wrong guess — triggers shake + flash. */
  wrongSignal?: number;
  /** Called once when all slots are filled. Receives the assembled string. */
  onSubmit: (guess: string) => void;
  /** Disable interaction (e.g. during loading or after correct). */
  disabled?: boolean;
}

/** Strip everything that isn't a Hebrew or Latin letter (or a word-separator
 *  space). Strips parenthetical version designators like "(ווקאלי)" / "(Live)"
 *  first (they're versions, not part of the title), then removes diacritics,
 *  niqqud, geresh/gershayim, parens, quotes, dashes, ampersands, etc. */
function stripDecorations(s: string): string {
  return s
    .replace(/\s*[\[(][^\])]*[\])]/g, "") // drop parenthetical version tags
    .normalize("NFD")
    .replace(/[\u0300-\u036f\u0591-\u05c7]/g, "") // diacritics + niqqud
    .replace(/[^\u05d0-\u05eaa-zA-Z\s]/g, "") // keep only HE/Latin letters + spaces
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Hebrew final-letter mapping: when the user types from a non-final tile,
 *  the slot still wants to look "natural", so we store and compare on the
 *  non-final form. Keeps the bank simple (single tile per phoneme). */
const HE_FINAL_TO_BASE: Record<string, string> = {
  ך: "כ",
  ם: "מ",
  ן: "נ",
  ף: "פ",
  ץ: "צ",
};
const baseLetter = (ch: string) => HE_FINAL_TO_BASE[ch] ?? ch;

const HE_FILLERS = ["ב", "ג", "ד", "ה", "ז", "ח", "ט", "י", "כ", "ל", "מ", "נ", "ס", "פ", "ק", "ר", "ש", "ת"];
const EN_FILLERS = "abcdefghijklmnopqrstuvwxyz".split("");

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Build the puzzle data for a target answer.
 *  Bank size = answer letter count × 2 (1:1 letters : fillers), so a 15-letter
 *  title gives ~30 tiles. Capped at 36 to keep the bank manageable on phone. */
function buildPuzzle(target: string, locale: "he" | "en") {
  const cleaned = stripDecorations(target);
  const words = cleaned.split(" ").filter(Boolean);
  const slots: { word: number; index: number; expected: string }[] = [];
  const letters: string[] = [];
  words.forEach((w, wi) => {
    for (let i = 0; i < w.length; i++) {
      const ch = baseLetter(w[i]);
      slots.push({ word: wi, index: i, expected: ch });
      letters.push(ch);
    }
  });
  const alphabet = locale === "he" ? HE_FILLERS : EN_FILLERS;
  // Filler count: roughly equal to answer length (so bank ≈ 2× answer size),
  // capped so the bank stays usable on a small viewport.
  const fillerCount = Math.min(letters.length, 36 - letters.length);
  const fillers: string[] = [];
  for (let i = 0; i < fillerCount; i++) {
    fillers.push(alphabet[Math.floor(Math.random() * alphabet.length)]);
  }
  const tiles = shuffle([...letters, ...fillers]).map((ch, idx) => ({
    id: idx,
    ch,
  }));
  return { slots, tiles, words };
}

export function LetterBank({
  correctText,
  locale,
  wrongSignal = 0,
  onSubmit,
  disabled = false,
}: LetterBankProps) {
  const puzzle = useMemo(
    () => buildPuzzle(correctText, locale),
    [correctText, locale]
  );

  // Each slot stores the tile id placed in it (or null).
  const [slotFills, setSlotFills] = useState<(number | null)[]>(
    () => Array(puzzle.slots.length).fill(null)
  );
  // Reset slots whenever the puzzle changes (new song).
  useEffect(() => {
    setSlotFills(Array(puzzle.slots.length).fill(null));
  }, [puzzle]);

  const [shake, setShake] = useState(false);
  const submittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!wrongSignal) return;
    setShake(true);
    // Empty all slots to let the player retry; bank tiles return automatically.
    const t1 = setTimeout(() => setShake(false), 500);
    const t2 = setTimeout(() => {
      setSlotFills(Array(puzzle.slots.length).fill(null));
      submittedRef.current = null;
    }, 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [wrongSignal, puzzle.slots.length]);

  const placedTileIds = new Set(slotFills.filter((id): id is number => id !== null));

  const placeTile = (tileId: number) => {
    if (disabled) return;
    if (placedTileIds.has(tileId)) return;
    const next = [...slotFills];
    const empty = next.findIndex((v) => v === null);
    if (empty === -1) return;
    next[empty] = tileId;
    setSlotFills(next);
  };

  const removeFromSlot = (slotIdx: number) => {
    if (disabled) return;
    const next = [...slotFills];
    next[slotIdx] = null;
    setSlotFills(next);
  };

  // Auto-submit once every slot is filled.
  useEffect(() => {
    // Guard: stale slotFills (more entries than the new puzzle has slots, or
    // tile-ids that don't exist anymore) should NOT trigger a submit.
    if (slotFills.length !== puzzle.slots.length) return;
    if (slotFills.some((v) => v === null)) return;
    if (slotFills.some((v) => v !== null && !puzzle.tiles[v])) return;
    const guess = puzzle.words
      .map((w, wi) => {
        let s = "";
        for (let i = 0; i < w.length; i++) {
          const slot = puzzle.slots.findIndex((sl) => sl.word === wi && sl.index === i);
          const tileId = slotFills[slot];
          const tile = tileId !== null ? puzzle.tiles[tileId] : null;
          s += tile ? tile.ch : "?";
        }
        return s;
      })
      .join(" ");
    if (submittedRef.current === guess) return;
    submittedRef.current = guess;
    onSubmit(guess);
  }, [slotFills, puzzle, onSubmit]);

  // Render — slot rows by word, then bank tiles.
  let runningSlotIdx = 0;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        animation: shake ? "nig-shake 0.4s" : undefined,
      }}
    >
      {/* Slots — one inline-flex group per word, with gaps between groups */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "center",
          direction: locale === "he" ? "rtl" : "ltr",
        }}
      >
        {puzzle.words.map((w, wi) => (
          <div
            key={wi}
            style={{
              display: "flex",
              gap: 4,
            }}
          >
            {Array.from({ length: w.length }).map((_, i) => {
              const slotIdx = runningSlotIdx++;
              const tileId = slotFills[slotIdx] ?? null;
              const tile = tileId !== null ? puzzle.tiles[tileId] : null;
              const filled = !!tile;
              return (
                <button
                  key={i}
                  onClick={() => filled && removeFromSlot(slotIdx)}
                  className="nig-tap"
                  style={{
                    width: 26,
                    height: 32,
                    borderRadius: 5,
                    background: filled ? T.creamWarm : `${T.cream}10`,
                    border: `1.5px solid ${filled ? T.gold : `${T.gold}50`}`,
                    fontFamily: T.serif,
                    fontSize: 17,
                    fontWeight: 700,
                    color: filled ? T.ink : T.mist,
                    cursor: filled ? "pointer" : "default",
                    padding: 0,
                  }}
                >
                  {tile ? tile.ch : ""}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bank — scrambled letter tiles */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          justifyContent: "center",
          padding: "10px 8px",
          background: `${T.navyMid}aa`,
          borderRadius: T.r2,
          border: `1px solid ${T.hairline}`,
          direction: locale === "he" ? "rtl" : "ltr",
        }}
      >
        {puzzle.tiles.map((tile) => {
          const used = placedTileIds.has(tile.id);
          return (
            <button
              key={tile.id}
              onClick={() => placeTile(tile.id)}
              disabled={disabled || used}
              className="nig-tap nig-press"
              style={{
                width: 26,
                height: 32,
                borderRadius: 5,
                background: used ? "transparent" : T.cream,
                color: used ? "transparent" : T.ink,
                border: `1px solid ${used ? T.hairline : T.creamWarm}`,
                fontFamily: T.serif,
                fontSize: 17,
                fontWeight: 700,
                cursor: used ? "default" : "pointer",
                padding: 0,
              }}
            >
              {tile.ch}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Title-only matching for letter-bank guesses. Strip decorations + final
 *  letters before comparing, since the bank only deals with base forms. */
export function letterBankMatches(
  correct: string,
  guessed: string
): boolean {
  const norm = (s: string) =>
    stripDecorations(s)
      .toLowerCase()
      .split("")
      .map(baseLetter)
      .join("");
  return norm(correct) === norm(guessed);
}
